/*
  Defers decorative <video> playback until the clip is near the viewport.

  Measured on AIR PRO (theme 197414650233, Lighthouse mobile): the page pulled 10.4 MB, of which
  8.2 MB was EIGHT autoplaying MP4s, and LCP sat at 7.2 s. Total blocking time was 0 ms and CLS
  0.001 — nothing was wrong with the JavaScript or the layout. The videos were simply competing
  with the hero image for bandwidth.

  `preload="metadata"` does not prevent this. `autoplay` overrides it: a muted autoplaying video
  starts fetching immediately and keeps buffering as it loops, whether or not it is on screen. Six
  of those eight clips sit thousands of pixels down the page.

  So the markup drops `autoplay`, sets `preload="none"` and adds `data-defer`; this file starts
  playback when the clip comes within 200px of the viewport and pauses it again on the way out —
  which also stops half a dozen videos decoding in the background on a phone.

  Degrades honestly: with no JavaScript, or no IntersectionObserver, the clips still exist and
  still have their `poster`; they just do not play. A missing decorative animation is a better
  failure than 8 MB nobody asked for.

  A video inside a hidden carousel panel never intersects, so it never loads until its panel is
  shown — which is why the attachment clips on s3 no longer all download at once either.

  ── Relationship to snippets/infra-deferred-video.liquid ─────────────────────────────
  That snippet solves the same problem for the Infra Pod and Halo bands, and its findings are
  worth reading before changing this file. It concluded that `preload` cannot be trusted — and
  it is right, FOR ITS CASE: its markup keeps `autoplay`, and a muted autoplaying video
  downloads regardless of preload. Dropping `autoplay` is the piece that makes `preload="none"`
  hold, which is why this file does that and does not need its `data-src` withholding.

  Verified on AIR PRO at 390px with 4x CPU and ~1.6 Mbps: 0 of 8 video files fetched before any
  scroll, against 8 files / 8.1 MB before. Keep the two mechanisms separate — that one needs
  `<source data-src>` children, and retrofitting 179 baked `<video src>` tags to match would be
  a far riskier edit to generated files than adding an attribute.
*/

(function () {
  // The script is referenced by several sections; only the first copy should bind.
  if (window.__pdpVideoDefer) return;
  window.__pdpVideoDefer = true;

  const start = (v) => {
    if (v.preload !== 'auto') v.preload = 'auto';
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };

  const bind = () => {
    /*
      Two selectors. `data-defer` is the attribute on hand-written markup; `.js-vdefer` is a
      marker CLASS, needed because Shopify's `video_tag` filter takes keyword arguments and
      `data-defer` is not a valid Liquid argument name — there is no way to pass a hyphenated
      attribute through it. Those calls set `autoplay: false, preload: 'none'` and add the class.
    */
    const vids = [...document.querySelectorAll('video[data-defer], video.js-vdefer')];
    if (!vids.length) return;

    if (!('IntersectionObserver' in window)) {
      vids.forEach(start);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) start(e.target);
          /*
            ⚠ DELIBERATELY NO `pause()` ON EXIT.
            It used to pause a clip on the way out, to stop half a dozen videos decoding in the
            background. That created a race the clips lost: `start()` sets preload and calls
            play() while readyState is still 0, and play() resolves only once there is data. If
            the observer delivers a "no longer intersecting" entry before that promise settles —
            which happens during any fast or programmatic scroll — the pending play is cancelled
            and the clip stays paused even after it comes back into view.

            Measured on Compression PRO: the hero clip reached readyState 4 with preload flipped
            to auto, so it had loaded and started, and still reported paused with currentTime
            stuck at 0.9s. On live the same clip carries plain `autoplay` and always plays. A
            black rectangle where a hero video should be is far worse than some background
            decoding, so reliability wins here.

            The expensive half of the saving is untouched: nothing downloads until it is
            approached, which is what took AIR PRO from 8 files / 8.1 MB before any scroll to
            zero.
          */
        }
      },
      // 75% of the viewport height, not an arbitrary pixel figure. That number was measured
      // elsewhere in this theme — see snippets/infra-deferred-video.liquid, which found 200%
      // swept in clips at initial load and below roughly half a viewport the poster was still
      // showing when the band arrived. Most of the clips this file covers have no poster, so
      // arriving late reads as a blank box; 75% gives ~675px of lead without costing first paint.
      { rootMargin: `${Math.round(window.innerHeight * 0.75)}px 0px` },
    );
    vids.forEach((v) => io.observe(v));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
